import {
  DemonstrativoReceitaEDespesaItem,
  FiltroRelatorioReceitaDespesa,
} from "core/features/relatorios/typings";
import {
  RelatorioDemonstrativoReceitaDespesaTotais,
  DemonstrativoReceitaEDespesaTransferencia,
} from "core/features/relatorios/typings";
import xlsx, { ISettings, IJsonSheet, IContent } from "json-as-xlsx";
import moment from "moment";
import { formatMoney } from "infra/helpers/number";
import { defaultContentMapperXlsx } from "infra/packages/json-as-xlsx/index";

export function exportExcelRelatorioDemonstrativoReceitaDespesa({
  receitas,
  despesas,
  filtro,
  totais,
  transferencias,
  somarSubcategorias,
}: {
  receitas: DemonstrativoReceitaEDespesaItem[];
  despesas: DemonstrativoReceitaEDespesaItem[];
  filtro: FiltroRelatorioReceitaDespesa;
  totais: RelatorioDemonstrativoReceitaDespesaTotais;
  transferencias?: DemonstrativoReceitaEDespesaTransferencia[];
  somarSubcategorias?: boolean;
}) {
  const settings: ISettings = {
    fileName: `Relatório Demonstrativo de Receitas e Despesas - ${moment(
      filtro.dataInicio
    ).format("DD_MM_YYYY")} a ${moment(filtro.dataFim).format("DD_MM_YYYY")}`,
    extraLength: 3,
    writeOptions: {},
  };

  const columnsReceitasDespesas = [
    {
      label: "Conta Categoria",
      value: (row: IContent) =>
        `${row.breadcrumb ? row.breadcrumb + "." : ""}${row.nome ?? ""}`,
    },
    {
      label: "Porcentagem",
      value: (row: IContent) => (!row.porcentagem ? "" : `${row.porcentagem}%`),
    },
    {
      label: "Valor",
      format: "#,###0.00",
      value: (row: IContent) =>
        somarSubcategorias && row.valorItems
          ? (+row.valor ?? 0) + (+row.valorItems ?? 0)
          : row.valor
          ? +row.valor
          : "",
    },
  ];

  const columnsTransferencias = getColumnsTransferencias();

  const contentMapper = (item: DemonstrativoReceitaEDespesaItem) => ({
    breadcrumb: item.breadcrumb,
    nome: item.nome,
    porcentagem: item.porcentagem,
    valor: item.valor,
    valorItems: item.items?.length ? item.valorItems : 0,
  });

  const contentFilter = (item: DemonstrativoReceitaEDespesaItem) =>
    !item.items.length || !!item.valor;

  const sheetsData: IJsonSheet[] = [
    {
      sheet: "Geral",
      columns: columnsReceitasDespesas,
      content: [{} as any, ...receitas, {} as any, ...despesas].map(
        contentMapper
      ),
    },
    {
      sheet: "Receitas",
      columns: columnsReceitasDespesas,
      content: receitas.filter(contentFilter).map(contentMapper),
    },
    {
      sheet: "Despesas",
      columns: columnsReceitasDespesas,
      content: despesas.filter(contentFilter).map(contentMapper),
    },
  ];

  if (transferencias?.length) {
    sheetsData.push({
      sheet: "Transferências",
      columns: columnsTransferencias,
      content: transferencias.map(defaultContentMapperXlsx),
    });
  }

  sheetsData.push({
    sheet: "Outros dados",
    content: [{}],
    columns: [
      {
        label: `Saldo em ${moment(filtro.dataInicio)
          .add(-1, "days")
          .format("DD/MM/YYYY")}${
          moment(filtro.dataInicio)
            .add("1", "days")
            .isAfter(moment().endOf("day"))
            ? " (Previsto)"
            : ""
        }`,
        value: () => `${formatMoney(totais.saldoInicial || 0)}`,
      },
      {
        label: `Saldo em ${moment(filtro.dataFim).format("DD/MM/YYYY")}${
          moment(filtro.dataFim).isAfter(moment().endOf("day"))
            ? " (Previsto)"
            : ""
        }`,
        value: () => `${formatMoney(totais.saldo || 0)}`,
      },
      {
        label: "Resultado",
        value: () => `${formatMoney(totais.resultado || 0)}`,
      },
    ],
  });

  xlsx(sheetsData, settings);
}

function getColumnsTransferencias() {
  return [
    {
      label: "Data",
      value: (row: IContent) =>
        `${row.data ? moment(row.data as string).format("DD/MM/YYYY") : ""}`,
    },
    {
      label: "Valor",
      format: "#,###0.00",
      value: (row: IContent) => (!row.valor ? "" : +row.valor),
    },
    {
      label: "Conta de Origem",
      value: (row: IContent) => (!row.contaOrigem ? "" : row.contaOrigem),
    },
    {
      label: "Conta de Destino",
      value: (row: IContent) => (!row.contaDestino ? "" : row.contaDestino),
    },
  ];
}
